<template>
  <div class="block mx-auto my-48 max-w-448">
    <SitterAlertBanner v-if="serverError" :error="serverError" />
    <PoppinsCard>
      <FormHeader />
      <form id="sitter-sign-up-form" novalidate @submit.prevent="validateAll">
        <PoppinsAccountCreateFields
          ref="accountCreateFields"
          :required-fields="requiredFields"
          :server-error="serverError"
          @update:model-value="handleAccountCreateInput"
          @reset-server-error="resetServerError"
        />
        <PhoneField ref="phoneField" @field-update="handleFieldUpdate" />
        <BirthDateFields ref="birthDateFields" @input="handleInput" />
        <GenderRadio ref="genderRadio" @input="handleInput" />
        <PoppinsTermsOfUse
          ref="termsOfUse"
          class="mb-24"
          terms-of-use-link="https://www.sittercity.com/terms"
          privacy-policy-link="https://www.sittercity.com/privacy"
          @input="handleInput"
        />
        <PoppinsButton
          id="formSubmit"
          :loading="loading"
          class="w-full"
          variant="primary"
          :value="'Submit'"
          size="medium"
          type="submit"
        />
      </form>
    </PoppinsCard>
    <PoppinsTrustAndSafety />
  </div>
</template>

<script lang="ts">
import { sendSegmentEvent, sendSegmentEventWithTimeout } from '@/lib/segment';
import { writeDomainCookie, readDomainCookie } from '@/lib/domain_cookie';
import { startSiftTracking } from '@/lib/sift';
import { generateSha1Hash } from '@/lib/crypto';
import { establishAuthenticatedSession } from '@/lib/establishAuthenticatedSession';
import { identifyAccountInSegmentOnce } from '@/lib/identifyAccountInSegmentOnce';
import { CREATE_SITTER } from '@/graphql/account/mutations';
import { ProviderFormData, RequiredFields } from './__types__/SitterCreateForm';
import PhoneField from './components/PhoneField.vue';
import BirthDateFields from './components/BirthDateFields.vue';
import GenderRadio from './components/GenderRadio.vue';
import SitterAlertBanner from './components/SitterAlertBanner.vue';
import SitterCreateFormHeader from './components/SitterCreateFormHeader.vue';
// Poppins components
import Card from '@/components/card/Card.vue';
import PoppinsAccountCreateFields from '@/components/accountCreateFields/AccountCreateFields.vue';
import PoppinsTermsOfUse from '@/components/termsOfUse/PoppinsTermsOfUse.vue';
import PoppinsTrustAndSafety from '@/components/trustAndSafety/PoppinsTrustAndSafety.vue';
import Button from '@/components/button/Button.vue';
import { consumeInvitedSitterToken } from '@/helpers/invitedSitterTokenHelper';
import { geocode, isValidZipcode } from '@/lib/geocode';

export default {
  name: 'SitterCreateForm',
  components: {
    PoppinsAccountCreateFields,
    PoppinsButton: Button,
    PoppinsCard: Card,
    PoppinsTermsOfUse,
    PoppinsTrustAndSafety,
    FormHeader: SitterCreateFormHeader,
    SitterAlertBanner,
    BirthDateFields,
    PhoneField,
    GenderRadio,
  },
  data(): {
    form: ProviderFormData;
    requiredFields: RequiredFields;
    serverError: Error | null;
    loading: Boolean;
  } {
    return {
      form: {
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        location: {
          x: null,
          y: null,
        },
        phoneNumber: '',
        birthDate: '',
        gender: '',
        termsAccepted: false,
      },
      city: null,
      zipCode: null,
      // tell Account Create Fields which fields are required, because this may differ from flow to flow
      requiredFields: ['firstName', 'lastName', 'email', 'password', 'zipcode', 'phone', 'gender', 'termsAccepted'],
      // if CreateSitter fails, alert user
      serverError: null,
      loading: false,
    };
  },
  mounted() {
    sendSegmentEvent('Registration Started', {
      registration_experience: 'direct',
      role: 'sitter',
    });
  },
  methods: {
    handleInput(key, value) {
      // generic input handler for child components that only emit one value
      this.form[key] = value;
    },
    handleFieldUpdate({ key, value }: { key: string; value: string }) {
      this.form[key] = value;
    },
    handleAccountCreateInput(accountCreateFields) {
      // map values from account create fields to the parent form
      Object.keys(this.form)
        // check if parent form has corresponding key, or the rest of the values will be set to undefined
        .filter((key) => !!accountCreateFields[key])
        // if the key exists both in parent form and in account create fields, set parent form value
        .forEach((key) => {
          this.form[key] = accountCreateFields[key];
        });
      // account create fields gives us a zipcode but we need location coordinates
      const zip = accountCreateFields?.zipcode;
      if (isValidZipcode(zip)) {
        this.syncLocationFromZipcode(zip);
      }
    },
    async syncLocationFromZipcode(zipcode: string) {
      const result = await geocode(zipcode);
      if (!result?.location) {
        this.form.location = { x: null, y: null };
        this.city = null;
        this.zipCode = null;
        return;
      }

      this.form.location = { ...result.location };
      this.city = result.city;
      this.zipCode = result.zipCode;
    },
    async validateAll() {
      // reset server error in case user is resubmitting
      this.resetServerError();
      // only submit if child components pass validation
      if (this.allChildrenAreValid()) {
        await this.createSitter();
      }
    },
    // validate all child components at once
    allChildrenAreValid() {
      this.$refs.accountCreateFields?.validateAll();
      this.$refs.phoneField?.validatePhone();
      this.$refs.birthDateFields?.validateAll();
      this.$refs.genderRadio?.validateGender();
      this.$refs.termsOfUse?.validate();

      return (
        this.$refs.accountCreateFields?.validateAll() &&
        this.$refs.phoneField?.validatePhone() &&
        this.$refs.birthDateFields?.validateAll() &&
        this.$refs.genderRadio?.validateGender() &&
        this.$refs.termsOfUse?.validate()
      );
    },
    async sitterRoleAssigned(sitterSso) {
      const { email, firstName, lastName } = this.form;

      if (window.dataLayer && email) {
        const emailSha1 = await generateSha1Hash(email);

        window.dataLayer.push({
          event: 'identify',
          email,
          emailSha1,
          customerID: sitterSso,
        });
      }

      await sendSegmentEventWithTimeout('Sitter Role Assigned', {
        user_id: sitterSso,
        event: 'Sitter Role Assigned',
        address: {
          city: this.city,
          country: 'USA',
          postalCode: this.zipCode,
        },
        corporateName: null,
        corporateSlug: null,
        createdAt: new Date(),
        email,
        familyType: null,
        firstName,
        isCorporate: false,
        isFirstResponder: false,
        isVolunteer: false,
        lastName,
        sitterType: 'basic',
      });
    },
    async linkInvitedSitterAndRedirect() {
      const invitedSitterToken = readDomainCookie('invitedSitterToken');
      const interviewRequestId = readDomainCookie('interviewRequestId');
      const invitedSitterId = readDomainCookie('invitedSitterId');

      if (invitedSitterToken && interviewRequestId && invitedSitterId) {
        try {
          await consumeInvitedSitterToken(interviewRequestId, invitedSitterToken, invitedSitterId);
          writeDomainCookie('skipProfileCreate', 'true');
        } catch (error) {
          console.error('Error consuming invited sitter token:', error);
        }
      }

      this.redirectToLocation();
      return true;
    },
    async createSitterAccount() {
      const response = await this.$apollo.mutate({
        mutation: CREATE_SITTER,
        variables: {
          input: this.form,
        },
      });

      const { token, refreshToken, account } = response?.data?.account?.createSitter ?? {};
      if (!token || !refreshToken) {
        console.error('Expected a jwt and refresh token in the response, but received: ', response);
        throw new Error('missing-login-tokens');
      }

      return {
        token,
        refreshToken,
        ssoUUID: account?.id,
      };
    },
    /**
     * Post-identify side effects for a newly created sitter. Runs after Segment identify
     * (with gbuuid when present) so GrowthBook can join geopage exposures to this conversion.
     */
    async onSitterSessionReady(ssoUUID: string) {
      window.sessionStorage.setItem('registrationID', ssoUUID);

      try {
        startSiftTracking();
      } catch (error) {
        console.warn('Sift setup failed:', error);
      }

      await this.sitterRoleAssigned(ssoUUID);
    },
    /**
     * Auth handoff after createSitter: persist tokens, load account, Segment identify,
     * then post-signup tracks. Layout identify only runs on full page load while anonymous.
     */
    async completeSitterSignupSession(created: { token: string; refreshToken: string; ssoUUID?: string }) {
      const account = await establishAuthenticatedSession({
        store: this.$store,
        tokens: { jwt: created.token, refresh: created.refreshToken },
      });

      // Tokens are already persisted; a failed account fetch must not abort onboarding
      // (createSitter succeeded — blocking here leaves users stuck with an email-taken retry).
      if (!account) {
        console.error('Account fetch failed after createSitter; continuing onboarding with createSitter identity');
        // establishAuthenticatedSession only identifies when account fetch succeeds — still stitch
        // GrowthBook exposures via gbuuid using the createSitter SSO id when Vuex account is missing.
        if (created.ssoUUID) {
          await identifyAccountInSegmentOnce({
            ssoUUID: created.ssoUUID,
            email: this.form?.email || '',
          });
        }
      }

      await this.onSitterSessionReady(account?.ssoUUID || created.ssoUUID);
      return account;
    },
    handleCreateSitterError(error: unknown) {
      if (error instanceof Error && error.message === 'Underage user') {
        writeDomainCookie('ineligible', 'true');
        this.redirectToUnderagePage();
        return;
      }

      if (error instanceof Error) {
        this.serverError = error;
        return;
      }

      console.error(error);
      this.serverError = new Error('unknown-error');
    },
    async createSitter() {
      this.loading = true;
      try {
        const created = await this.createSitterAccount();
        await this.completeSitterSignupSession(created);
        await this.linkInvitedSitterAndRedirect();
      } catch (error) {
        this.loading = false;
        this.handleCreateSitterError(error);
      }
    },
    redirectToLocation() {
      this.$router.push({ path: '/sitter/signup/address' });
    },
    redirectToUnderagePage() {
      window.location.href = '/locked_out';
    },
    resetServerError() {
      this.serverError = null;
    },
  },
};
</script>
